import type { Dispatch, SetStateAction } from "react"
import type { OvalClubSettings } from "@/lib/ovalClubSettings"
import { adminTreatyClient } from "@/contexts/auth-api/treaty-client"

type UserRole = "user" | "super_admin" | "support"

interface InvestigationSessionShape {
  isActive: boolean
  mode: "investigate"
  actorUserId: string
  actorRole: UserRole
  actorEmail: string
}

interface AmaEvent {
  _id: string
  title: string
  description: string
  hostName: string
  startsAt: string
  durationMinutes: number
  joinUrl: string
  streamUrl: string | null
  sendToAll: boolean
  agencyId: string | null
  agencyName: string | null
  createdByName: string
  createdAt: string
}

interface StoreProduct {
  _id: string
  name: string
  description: string
  category: string
  sku: string
  badge: string
  priceUsd: number
  stock: number
  imageUrls: string[]
  primaryImageUrl: string | null
  inStock: boolean
  isActive: boolean
  featured: boolean
  maxPerOrder: number | null
  createdAt: string
  updatedAt: string
}

interface AdminOverviewMetrics {
  totalUsers: number
  totalDeals: number
  activeAgencies: number
  pendingApprovals: number
  estimatedRevenue: number
  newUsersThisWeek: number
  approvalRate: number
  avgDealsPerAgent: number
}

interface AdminDealVolumeEntry {
  date: string
  approved: number
  pending: number
  rejected: number
}

type AdminDealAnalyticsData = Record<string, unknown>

interface AdminPendingDeal {
  _id: string
  memberId: string
  submitterName: string
  submitterEmail: string
  createdAt: string
  pointsEarned: number
}

interface AdminAgentPerformanceEntry {
  _id: string
  name: string
  email: string
  agencyName: string
  weeklyDeals: number
  dailyDeals: number
  monthlyDeals: number
  totalDeals: number
  points: number
}

interface AdminAgencyInfo {
  agencyId: string
  name: string
  ownerEmail: string
  memberCount: number
  monthlyDeals: number
  dailyDeals: number
  totalDeals: number
}

interface AdminAgenciesPaginatedResult {
  success: boolean
  agencies?: AdminAgencyInfo[]
  total?: number
  page?: number
  limit?: number
  totalPages?: number
  message?: string
}

interface GameEvent {
  _id: string
  title: string
  description: string
  slug: string
  eventType: "action_challenge" | "points_multiplier" | "raffle" | "king_of_hill" | "custom"
  status: "draft" | "coming_soon" | "live" | "ended"
  isFeatured: boolean
  startDate: string | null
  endDate: string | null
  imageUrl: string | null
  detailImageUrl: string | null
  rewards: Array<{ type: string; amount: number; label: string }>
  actionRequirements: Array<{ type: string; target: number; metric: string }>
  pointsMultiplier: number | null
  badge: string
  badgeColor: string
  iconName: string
  participantCount: number
  createdAt: string
}

interface AdminApiDeps<TUser> {
  getToken: () => string | null
  setUser: Dispatch<SetStateAction<TUser | null>>
  setInvestigation: Dispatch<SetStateAction<InvestigationSessionShape | null>>
  saveToken: (token: string) => void
  clearToken: () => void
  saveInvestigateOriginToken: (originToken: string) => void
  getInvestigateOriginToken: () => string | null
  clearInvestigateOriginToken: () => void
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

function pickTreatyPayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value

  const direct = value as Record<string, unknown>
  if (typeof direct.success === "boolean") return direct

  for (const nested of Object.values(direct)) {
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).success === "boolean") {
      return nested
    }
  }

  return value
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const data = pickTreatyPayload(response.data)
  if (data && typeof data === "object") return data as T

  const errorValue = pickTreatyPayload(response.error?.value)
  if (errorValue && typeof errorValue === "object") {
    return errorValue as T
  }

  return { success: false, message: fallbackMessage } as T
}

function getNotAuthenticatedResult<T extends { success: boolean; message?: string }>(message = "Not authenticated"): T {
  return { success: false, message } as T
}

export function createAdminApi<TUser>(deps: AdminApiDeps<TUser>) {
  const getAdminSession = async (): Promise<{
    success: boolean
    role?: UserRole
    permissions?: {
      canViewAdminDashboard: boolean
      canManageUsers: boolean
      canModerateContent: boolean
      canManageSystemSettings: boolean
    }
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.session.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = response.data ?? (
        response.error?.value && typeof response.error.value === "object"
          ? response.error.value as {
            success: boolean
            role?: UserRole
            permissions?: {
              canViewAdminDashboard: boolean
              canManageUsers: boolean
              canModerateContent: boolean
              canManageSystemSettings: boolean
            }
            message?: string
          }
          : { success: false, message: "Failed to get admin session" }
      )

      return {
        success: data.success,
        role: data.role,
        permissions: data.permissions,
        message: data.message,
      }
    } catch (error) {
      console.error("Get admin session error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getAdminAgencyTargets = async (): Promise<{
    success: boolean
    agencies?: Array<{ agencyId: string; agencyName: string }>
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.agencies.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = response.data ?? (
        response.error?.value && typeof response.error.value === "object"
          ? response.error.value as {
            success: boolean
            agencies?: Array<{ agencyId: string; agencyName: string }>
            message?: string
          }
          : { success: false, message: "Failed to get admin agency targets" }
      )

      return {
        success: data.success,
        agencies: data.agencies,
        message: data.message,
      }
    } catch (error) {
      console.error("Get admin agency targets error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const searchAdminUsers = async (query: string): Promise<{
    success: boolean
    users?: Array<{
      _id: string
      email: string
      displayName: string
      agencyName: string | null
      userType: "agent" | "solo_agent" | "agency" | null
      hasCompletedOnboarding: boolean
      emailVerified: boolean
      createdAt: string
    }>
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    const trimmedQuery = query.trim()
    if (trimmedQuery.length < 2) {
      return { success: false, message: "Search must be at least 2 characters" }
    }

    try {
      const response = await adminTreatyClient.api.admin.users.search.get({
        query: { q: trimmedQuery },
        headers: { authorization: `Bearer ${token}` },
      })
      const data = response.data ?? (
        response.error?.value && typeof response.error.value === "object"
          ? response.error.value as {
            success: boolean
            users?: Array<{
              _id: string
              email: string
              displayName: string
              agencyName: string | null
              userType: "agent" | "solo_agent" | "agency" | null
              hasCompletedOnboarding: boolean
              emailVerified: boolean
              createdAt: string
            }>
            message?: string
          }
          : { success: false, message: "Failed to search admin users" }
      )

      return {
        success: data.success,
        users: data.users,
        message: data.message,
      }
    } catch (error) {
      console.error("Search admin users error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const startInvestigateMode = async (targetUserId: string): Promise<{
    success: boolean
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    if (!targetUserId.trim()) {
      return { success: false, message: "Target user is required" }
    }

    try {
      const response = await adminTreatyClient.api.admin.investigate.start.post({ targetUserId }, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        token?: string
        user?: TUser
        investigation?: InvestigationSessionShape | null
        message?: string
      }>(response, "Failed to start investigate mode")

      if (data.success && data.token && data.user) {
        deps.saveInvestigateOriginToken(token)
        deps.saveToken(data.token)
        deps.setUser(data.user)
        deps.setInvestigation(data.investigation || null)
        return { success: true, message: data.message }
      }

      return { success: false, message: data.message || "Failed to start investigate mode" }
    } catch (error) {
      console.error("Start investigate mode error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const endInvestigateMode = async (): Promise<{
    success: boolean
    message?: string
  }> => {
    const originToken = deps.getInvestigateOriginToken()
    if (!originToken) {
      return { success: false, message: "No investigate session found" }
    }

    try {
      const response = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${originToken}` },
      })
      const data = await response.json() as {
        success: boolean
        user?: TUser
        message?: string
      }

      if (data.success && data.user) {
        deps.clearInvestigateOriginToken()
        deps.saveToken(originToken)
        deps.setUser(data.user)
        deps.setInvestigation(null)
        return { success: true, message: "Investigate mode ended" }
      }

      deps.clearInvestigateOriginToken()
      deps.clearToken()
      deps.setUser(null)
      deps.setInvestigation(null)
      return { success: false, message: data.message || "Original admin session expired. Please sign in again." }
    } catch (error) {
      console.error("End investigate mode error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const postAdminSystemChatMessage = async (payload: {
    content: string
    sendToAll: boolean
    agencyId?: string
    fileUrl?: string
    fileType?: string
    fileName?: string
    ctas?: Array<{ label: string; url: string }>
  }): Promise<{
    success: boolean
    message?: string
    postedCount?: number
    agencyIds?: string[]
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.chat["system-message"].post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        message?: string
        postedCount?: number
        agencyIds?: string[]
      }>(response, "Failed to post admin system chat message")

      return {
        success: data.success,
        message: data.message,
        postedCount: data.postedCount,
        agencyIds: data.agencyIds,
      }
    } catch (error) {
      console.error("Post admin system chat message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const createAdminAmaEvent = async (payload: {
    title: string
    description?: string
    hostName: string
    startsAt: string
    durationMinutes?: number
    joinUrl: string
    streamUrl?: string
    sendToAll?: boolean
    agencyId?: string
  }): Promise<{
    success: boolean
    event?: AmaEvent
    postedCount?: number
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.events.post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        event?: AmaEvent
        postedCount?: number
        message?: string
      }>(response, "Failed to create admin AMA event")

      return {
        success: data.success,
        event: data.event,
        postedCount: data.postedCount,
        message: data.message,
      }
    } catch (error) {
      console.error("Create admin AMA event error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminStoreProducts = async (): Promise<{
    success: boolean
    products?: StoreProduct[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.store.products.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        products?: StoreProduct[]
        message?: string
      }>(response, "Failed to get admin store products")

      return { success: data.success, products: data.products, message: data.message }
    } catch (error) {
      console.error("Get admin store products error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const saveAdminStoreProduct = async (payload: {
    _id?: string
    name: string
    description?: string
    category?: string
    sku?: string
    badge?: string
    priceUsd: number
    stock: number
    imageUrls?: string[]
    isActive?: boolean
    featured?: boolean
    maxPerOrder?: number | null
  }): Promise<{
    success: boolean
    product?: StoreProduct
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.store.products.post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        product?: StoreProduct
        message?: string
      }>(response, "Failed to save admin store product")

      return { success: data.success, product: data.product, message: data.message }
    } catch (error) {
      console.error("Save admin store product error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminFilterOptions = async (): Promise<{
    success: boolean
    agents?: Array<{ _id: string; name: string; email: string }>
    agencies?: Array<{ agencyId: string; agencyName: string }>
    states?: string[]
    userTypes?: string[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin["filter-options"].get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        agents?: Array<{ _id: string; name: string; email: string }>
        agencies?: Array<{ agencyId: string; agencyName: string }>
        states?: string[]
        userTypes?: string[]
        message?: string
      }>(response, "Failed to get admin filter options")

      return {
        success: data.success,
        agents: data.agents,
        agencies: data.agencies,
        states: data.states,
        userTypes: data.userTypes,
        message: data.message,
      }
    } catch (error) {
      console.error("Get admin filter options error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminOverview = async (
    startDate?: string,
    endDate?: string,
    agentId?: string,
    agencyId?: string,
    state?: string,
    userType?: string
  ): Promise<{
    success: boolean
    metrics?: AdminOverviewMetrics
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.overview.get({
        headers: { authorization: `Bearer ${token}` },
        query: {
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(agentId ? { agentId } : {}),
          ...(agencyId ? { agencyId } : {}),
          ...(state ? { state } : {}),
          ...(userType ? { userType } : {}),
        },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        metrics?: AdminOverviewMetrics
        message?: string
      }>(response, "Failed to get admin overview")

      return { success: data.success, metrics: data.metrics, message: data.message }
    } catch (error) {
      console.error("Get admin overview error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminDealVolume = async (
    days?: number,
    agentId?: string,
    agencyId?: string,
    state?: string,
    userType?: string
  ): Promise<{
    success: boolean
    data?: AdminDealVolumeEntry[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.deals.volume.get({
        headers: { authorization: `Bearer ${token}` },
        query: {
          ...(days ? { days: String(days) } : {}),
          ...(agentId ? { agentId } : {}),
          ...(agencyId ? { agencyId } : {}),
          ...(state ? { state } : {}),
          ...(userType ? { userType } : {}),
        },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        data?: AdminDealVolumeEntry[]
        message?: string
      }>(response, "Failed to get admin deal volume")

      return { success: data.success, data: data.data, message: data.message }
    } catch (error) {
      console.error("Get admin deal volume error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminDealAnalytics = async (): Promise<{
    success: boolean
    data?: AdminDealAnalyticsData
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.deals.analytics.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        data?: AdminDealAnalyticsData
        message?: string
      }>(response, "Failed to get admin deal analytics")

      return { success: data.success, data: data.data, message: data.message }
    } catch (error) {
      console.error("Get admin deal analytics error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminPendingDeals = async (): Promise<{
    success: boolean
    deals?: AdminPendingDeal[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.deals.pending.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        deals?: AdminPendingDeal[]
        message?: string
      }>(response, "Failed to get admin pending deals")

      return { success: data.success, deals: data.deals, message: data.message }
    } catch (error) {
      console.error("Get admin pending deals error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const approveOrRejectDeal = async (
    dealId: string,
    action: "approve" | "reject"
  ): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.deals.action.post({ dealId, action }, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        message?: string
      }>(response, "Failed to update deal status")

      return { success: data.success, message: data.message }
    } catch (error) {
      console.error("Approve/reject deal error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminAgentPerformance = async (): Promise<{
    success: boolean
    agents?: AdminAgentPerformanceEntry[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.agents.performance.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        agents?: AdminAgentPerformanceEntry[]
        message?: string
      }>(response, "Failed to get admin agent performance")

      return { success: data.success, agents: data.agents, message: data.message }
    } catch (error) {
      console.error("Get admin agent performance error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminAgencyOverview = async (): Promise<{
    success: boolean
    agencies?: AdminAgencyInfo[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.agencies.overview.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        agencies?: AdminAgencyInfo[]
        message?: string
      }>(response, "Failed to get admin agency overview")

      return { success: data.success, agencies: data.agencies, message: data.message }
    } catch (error) {
      console.error("Get admin agency overview error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminAgenciesPaginated = async (params: {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: "asc" | "desc"
  } = {}): Promise<AdminAgenciesPaginatedResult> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.agencies.paginated.get({
        headers: { authorization: `Bearer ${token}` },
        query: {
          ...(params.page ? { page: String(params.page) } : {}),
          ...(params.limit ? { limit: String(params.limit) } : {}),
          ...(params.search ? { search: params.search } : {}),
          ...(params.sortBy ? { sortBy: params.sortBy } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
      })

      return unwrapTreatyResult<AdminAgenciesPaginatedResult>(response, "Failed to get admin agencies")
    } catch (error) {
      console.error("Get admin agencies paginated error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminChatActivity = async (): Promise<{
    success: boolean
    data?: {
      byAgency: Array<{ agencyId: string; agencyName: string; messageCount: number }>
      dailyVolume: Array<{ date: string; count: number }>
    }
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.chat.activity.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        byAgency: Array<{ agencyId: string; agencyName: string; messageCount: number }>
        dailyVolume: Array<{ date: string; count: number }>
        message?: string
      }>(response, "Failed to get admin chat activity")

      if (data.success) {
        return { success: true, data: { byAgency: data.byAgency, dailyVolume: data.dailyVolume } }
      }

      return { success: false, message: data.message }
    } catch (error) {
      console.error("Get admin chat activity error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminChatControl = async (): Promise<{
    success: boolean
    control?: {
      systemOnlyMode: boolean
      systemOnlyUntil: string | null
      reason: string | null
    }
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.chat.control.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        control?: {
          systemOnlyMode: boolean
          systemOnlyUntil: string | null
          reason: string | null
        }
        message?: string
      }>(response, "Failed to get admin chat control")

      return { success: data.success, control: data.control, message: data.message }
    } catch (error) {
      console.error("Get admin chat control error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const setAdminChatControl = async (payload: {
    systemOnlyMode: boolean
    durationMinutes?: number
    systemOnlyUntil?: string
    reason?: string
  }): Promise<{
    success: boolean
    control?: {
      systemOnlyMode: boolean
      systemOnlyUntil: string | null
      reason: string | null
    }
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.chat.control.post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        control?: {
          systemOnlyMode: boolean
          systemOnlyUntil: string | null
          reason: string | null
        }
        message?: string
      }>(response, "Failed to set admin chat control")

      return { success: data.success, control: data.control, message: data.message }
    } catch (error) {
      console.error("Set admin chat control error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const adjustAdminUserEconomics = async (payload: {
    email: string
    pointsDelta?: number
    bonusPendingDelta?: number
    bonusAvailableDelta?: number
    note?: string
  }): Promise<{
    success: boolean
    user?: {
      _id: string
      email: string
      pointsCurrent: number
      pointsLifetime: number
      bonusPending: number
      bonusAvailable: number
    }
    applied?: {
      pointsDelta: number
      bonusPendingDelta: number
      bonusAvailableDelta: number
      note: string | null
    }
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.users["manual-adjust"].post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        user?: {
          _id: string
          email: string
          pointsCurrent: number
          pointsLifetime: number
          bonusPending: number
          bonusAvailable: number
        }
        applied?: {
          pointsDelta: number
          bonusPendingDelta: number
          bonusAvailableDelta: number
          note: string | null
        }
        message?: string
      }>(response, "Failed to adjust admin user economics")

      return {
        success: data.success,
        user: data.user,
        applied: data.applied,
        message: data.message,
      }
    } catch (error) {
      console.error("Adjust admin user economics error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminEconomicsSettings = async (): Promise<{
    success: boolean
    settings?: OvalClubSettings
    updatedAt?: string
    updatedBy?: string | null
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.economics.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        settings?: OvalClubSettings
        updatedAt?: string
        updatedBy?: string | null
        message?: string
      }>(response, "Failed to get admin economics settings")

      return {
        success: data.success,
        settings: data.settings,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
        message: data.message,
      }
    } catch (error) {
      console.error("Get admin economics settings error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const updateAdminEconomicsSettings = async (settings: OvalClubSettings): Promise<{
    success: boolean
    settings?: OvalClubSettings
    updatedAt?: string
    updatedBy?: string | null
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.economics.post({ settings }, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        settings?: OvalClubSettings
        updatedAt?: string
        updatedBy?: string | null
        message?: string
      }>(response, "Failed to update admin economics settings")

      return {
        success: data.success,
        settings: data.settings,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
        message: data.message,
      }
    } catch (error) {
      console.error("Update admin economics settings error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminGameEvents = async (): Promise<{
    success: boolean
    events?: GameEvent[]
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin["game-events"].get({
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        events?: GameEvent[]
        message?: string
      }>(response, "Failed to get admin game events")
    } catch (error) {
      console.error("Get admin game events error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const createAdminGameEvent = async (payload: Partial<GameEvent> & { eventType: string }): Promise<{
    success: boolean
    event?: GameEvent
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin["game-events"].post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        event?: GameEvent
        message?: string
      }>(response, "Failed to create admin game event")
    } catch (error) {
      console.error("Create admin game event error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const updateAdminGameEvent = async (eventId: string, payload: Partial<GameEvent>): Promise<{
    success: boolean
    event?: GameEvent
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin["game-events"]({ eventId }).put(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        event?: GameEvent
        message?: string
      }>(response, "Failed to update admin game event")
    } catch (error) {
      console.error("Update admin game event error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const announceGameEvent = async (
    eventId: string,
    opts?: { content?: string; fileUrl?: string; fileType?: string; fileName?: string }
  ): Promise<{
    success: boolean
    postedCount?: number
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin["game-events"]({ eventId }).announce.post(opts || {}, {
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        postedCount?: number
        message?: string
      }>(response, "Failed to announce game event")
    } catch (error) {
      console.error("Announce game event error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const adminAirdropBalance = async (payload: {
    amount: number
    currency?: "cash" | "points"
    target: "all" | "specific"
    emails?: string[]
    claimDeadlineHours?: number | null
    note?: string
  }): Promise<{
    success: boolean
    message?: string
    airdrop?: {
      amount: number
      currency: "cash" | "points"
      target: "all" | "specific"
      usersAffected: number
      claimDeadlineHours: number | null
      note: string | null
    }
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.airdrop.post(payload, {
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        message?: string
        airdrop?: {
          amount: number
          currency: "cash" | "points"
          target: "all" | "specific"
          usersAffected: number
          claimDeadlineHours: number | null
          note: string | null
        }
      }>(response, "Failed to run admin airdrop")
    } catch (error) {
      console.error("Admin airdrop error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminRedemptions = async (statusFilter?: "pending" | "processing" | "completed" | "rejected"): Promise<{
    success: boolean
    message?: string
    redemptions?: Array<{
      _id: string
      userId: string
      userEmail: string
      userDisplayName: string
      amount: number
      paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal"
      paymentHandle: string
      status: "pending" | "processing" | "completed" | "rejected"
      rejectionReason?: string
      createdAt: string
      processedAt?: string
      processedByEmail?: string
    }>
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.redemptions.get({
        headers: { authorization: `Bearer ${token}` },
        query: statusFilter ? { status: statusFilter } : {},
      })

      return unwrapTreatyResult<{
        success: boolean
        message?: string
        redemptions?: Array<{
          _id: string
          userId: string
          userEmail: string
          userDisplayName: string
          amount: number
          paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal"
          paymentHandle: string
          status: "pending" | "processing" | "completed" | "rejected"
          rejectionReason?: string
          createdAt: string
          processedAt?: string
          processedByEmail?: string
        }>
      }>(response, "Failed to get admin redemptions")
    } catch (error) {
      console.error("Get admin redemptions error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const updateRedemptionStatus = async (
    redemptionId: string,
    status: "pending" | "processing" | "completed" | "rejected",
    rejectionReason?: string
  ): Promise<{
    success: boolean
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return getNotAuthenticatedResult()

    try {
      const response = await adminTreatyClient.api.admin.redemptions.update.post({
        redemptionId,
        status,
        rejectionReason,
      }, {
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        message?: string
      }>(response, "Failed to update redemption status")
    } catch (error) {
      console.error("Update redemption status error:", error)
      return { success: false, message: "Network error" }
    }
  }

  return {
    getAdminSession,
    getAdminAgencyTargets,
    searchAdminUsers,
    startInvestigateMode,
    endInvestigateMode,
    postAdminSystemChatMessage,
    createAdminAmaEvent,
    getAdminStoreProducts,
    saveAdminStoreProduct,
    getAdminFilterOptions,
    getAdminOverview,
    getAdminDealVolume,
    getAdminDealAnalytics,
    getAdminPendingDeals,
    approveOrRejectDeal,
    getAdminAgentPerformance,
    getAdminAgencyOverview,
    getAdminAgenciesPaginated,
    getAdminChatActivity,
    getAdminChatControl,
    setAdminChatControl,
    adjustAdminUserEconomics,
    getAdminEconomicsSettings,
    updateAdminEconomicsSettings,
    getAdminGameEvents,
    createAdminGameEvent,
    updateAdminGameEvent,
    announceGameEvent,
    adminAirdropBalance,
    getAdminRedemptions,
    updateRedemptionStatus,
  }
}
